/**
 * `/s/:date` — the short, date-anchored link used by the evening SMS digest.
 *
 * The digest is about TOMORROW, so its link must open that day rather than
 * `/today`. The path is deliberately short (it competes for SMS units) and
 * lives on our own domain: a third-party shortener would risk US carrier
 * filtering and the message might never arrive.
 *
 * `?c=1` is the digest's "quiet day" flag: nothing is scheduled, the SMS
 * closed with "Tap to plan your day", so land on the add form rather than an
 * empty schedule. It maps onto `create=true`, which `ScheduleTab` already
 * reads — and already gates on `canEdit`, so a member without write access
 * simply lands on the schedule. Named `c` rather than `create` because the
 * whole URL competes for SMS units.
 *
 * Mounted inside `ProtectedRoute`, so a tap with no session is stashed by
 * `savePostAuthRedirect` (which preserves the query string) and replayed
 * after Google sign-in with both the date and the flag intact.
 */

import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { isValidDateKey } from '@/lib/dateKey';

export function ScheduleDateDeepLink() {
  const { date } = useParams<{ date: string }>();
  const [searchParams] = useSearchParams();
  // The flag rides along with the date: without a valid date there is nothing
  // coherent to prefill the form with, so it is dropped alongside it.
  const wantsCreate = searchParams.get('c') === '1';
  const to = isValidDateKey(date)
    ? `/schedule?date=${date}${wantsCreate ? '&create=true' : ''}`
    : '/schedule';
  return (
    <Navigate
      to={to}
      replace
    />
  );
}
